import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Container, Row, Col, Button, ListGroup, Spinner, Tab, Tabs, Alert } from 'react-bootstrap';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { DataContext } from '../../adapter';

import { ClaimTypeGetService, ClaimDocTypeGetService, DocPerClaimTypeGetService } from '../../domain';

const StyledContainer = styled(Container)`
    font-family: 'Montserrat';
    color: rgba(41, 37, 97, 1);
    background: rgb(234, 222, 208, 1);
    padding: 20px 20%;

    @media (min-width: 300px) and (max-width: 500px) {
        width: 100%;
        padding: 2% 10%;
    }
`;

const Title = styled.h1`
    font-size: 40px;
    font-weight: 700;
`;

const Description = styled.p`
    font-size: 22px;
    font-weight: 500;
`;

const DescriptionLite = styled.p`
    font-size: 16px;
    color: #4b4b4b;
`;

const DescriptionLite2 = styled.p`
    font-size: 14px;
    color: rgba(41, 37, 97, 1);
    padding-top: 14px;
`;

const StyledForm = styled(Form)`
    .form-group-label {
        font-size: 20px;
        font-weight: 500;
        padding: 5px 0px;
    }
`;

const StyledSubmitButton = styled(Button)`
    background-color: rgba(176, 17, 22, 1);
    max-width: fit-content;
    padding: 5px 40px;
    border-radius: 25px;
    font-family: 'Montserrat';
    font-weight: 700;
    font-size: 18px;
    color: white;
    text-align: center;

    @media (min-width: 300px) and (max-width: 500px) {
        font-size: 14px;
        padding: 5px 20px;
    }
`;

const StyledSubmitButton2 = styled(Button)`
    background-color: rgba(176, 17, 22, 1);
    max-width: fit-content;
    padding: 5px 60px;
    border-radius: 25px;
    font-family: 'Montserrat';
    font-weight: 700;
    font-size: 18px;
    color: white;
    text-align: center;

    @media (min-width: 300px) and (max-width: 500px) {
        font-size: 14px;
        padding: 5px 20px;
    }
`;

const InfoContainer = styled.div`
    position: relative;
    display: inline-block;
    cursor: pointer;

    &:hover .info-description {
        display: block;
    }
`;

const StyledDescription = styled.p`
    font-style: italic;
    font-size: 12px;
    color: black;
    display: none;
    position: absolute;
    background: rgba(255, 255, 255, 1);
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    width: 650px;
    z-index: 100;
    top: -60px;
    left: -300px;

    @media (min-width: 300px) and (max-width: 500px) {
        width: 350px;
        left: -290px;
        top: -90px;
    }
`;

const Loading = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.5);
    z-index: 2000;
`;

const StyleTitle = styled.h3`
    font-size: 24px;
    font-weight: 700;
`;

const StyleText = styled.p`
    font-weight: 500;
    padding: 0;
    margin: 0;

    i {
        margin-right: 6px; // Space between icon and text
        font-size: 20px; // Adjust the size of the icon
        font-weight: 700; // Adjust the font-weight for the icon (might not have effect depending on the icon font)
    }
`;

export default function Selection() {
    const [claimTypes, setClaimTypes] = useState([]);
    const [selectedDocDescs, setSelectedDocDescs] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [selectedClaimType, setSelectedClaimType] = useState('');
    const { claimsData, updateClaimsData } = DataContext();
    const [docPerClaimTypes, setDocPerClaimTypes] = useState(null);

    const _ClaimTypeGetService = new ClaimTypeGetService();
    const _ClaimDocTypeGetService = new ClaimDocTypeGetService();
    const _DocPerClaimTypeGetService = new DocPerClaimTypeGetService();

    const mapDocumentPerClaimType = async () => {
        try {
            setLoading(true);
            const [docPerClaimTypes, claimTypes, documentTypes] = await Promise.all([
                _DocPerClaimTypeGetService.execute(),
                _ClaimTypeGetService.execute(),
                _ClaimDocTypeGetService.execute()
            ]);

            if (!docPerClaimTypes.documentPerClaimTypes || !claimTypes.claimTypes || !documentTypes.documentTypes) {
                Swal.fire({
                    text: 'Something went wrong. Please try again later.',
                    icon: 'error',
                    iconColor: 'black',
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Ok',
                    customClass: {
                        popup: 'my-popup',
                        confirmButton: 'my-confirm-button',
                        cancelButton: 'my-cancel-button'
                    }
                });
                setLoading(false);
                return;
            }
            setClaimTypes(claimTypes.claimTypes);

            // map the data
            const result = docPerClaimTypes.documentPerClaimTypes.map((docPerClaim) => {
                const claimType = claimTypes.claimTypes.find((claim) => claim.id === docPerClaim.mtnClaimTypeId);
                const documentType = documentTypes.documentTypes.find(
                    (doc) => doc.id === docPerClaim.mtnDocumentTypeId
                );

                return {
                    id: docPerClaim.id,
                    mtnClaimTypeId: docPerClaim.mtnClaimTypeId,
                    mtnDocumentTypeId: docPerClaim.mtnDocumentTypeId,
                    claimTypeDesc: claimType ? claimType.claimTypeDesc : null,
                    claimTypeCd: claimType ? claimType.claimTypeCd : null,
                    documentDesc: documentType ? documentType.documentDesc : null
                };
            });
            // console.log('result', result);

            setDocPerClaimTypes(result);

            setLoading(false);
        } catch (error) {
            Swal.fire({
                text: 'Something went wrong. Please try again later.',
                icon: 'error',
                iconColor: 'black',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Ok',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            });
            console.error('Error:', error);
            setLoading(false);
        }
    };

    // Function to handle when claim type is selected
    const handleClaimTypeChange = (selectedOption) => {
        console.log('Selected Claim Type:', selectedOption);
        setSelectedClaimType(selectedOption);
        const selectedDocs = docPerClaimTypes
            .filter((doc) => doc.mtnClaimTypeId === selectedOption.value)
            .map((doc) => doc.documentDesc);
        setSelectedDocDescs(selectedDocs);
    };

    // Map claim types for Select component
    const claimTypeOptions = claimTypes.map((claimType) => ({
        value: claimType.id,
        label: claimType.claimTypeDesc
    }));

    const handleFormSubmit = () => {
        try {
            if (!selectedClaimType) {
                Swal.fire({
                    text: 'Please select a claim type before submitting.',
                    icon: 'warning',
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Ok',
                    customClass: {
                        popup: 'my-popup',
                        confirmButton: 'my-confirm-button',
                        cancelButton: 'my-cancel-button'
                    }
                });
                return;
            }

            const filteredDocuments = docPerClaimTypes.filter(item => item.mtnClaimTypeId === selectedClaimType.value);

            const mappedDocuments = filteredDocuments.map((item) => ({
                ...item,
                attachedDocuments: []
            }));
            console.log("mappedDocuments", mappedDocuments);
            // const mappedDocuments = docPerClaimTypes.filter((item) => {
            //     return item.mtnClaimTypeId;
            // });

            const newClaimsData = {
                claimsPayload: {
                    ...claimsData.claimsPayload,
                    mtnClaimTypeId: selectedClaimType.value,
                    claimDocuments: mappedDocuments
                }
            };
            updateClaimsData(newClaimsData);
            setLoading(true);

            setTimeout(() => {
                setLoading(false);
                navigate('/details');
            }, 1000); // 1-second delay before navigating to the next page
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        const savedClaimData = claimsData;

        if (savedClaimData && savedClaimData.claimsPayload.mtnClaimTypeId && claimTypes.length > 0) {
            // Find the corresponding claim type option based on saved mtnClaimTypeId
            const savedClaimType = claimTypeOptions.find(
                (option) => option.value === savedClaimData.claimsPayload.mtnClaimTypeId // Adjusted here
            );

            // Set the saved claim type as selected
            if (savedClaimType) {
                setSelectedClaimType(savedClaimType);

                // Optionally, set the documents related to the selected claim type
                const selectedDocs = docPerClaimTypes
                    .filter((doc) => doc.mtnClaimTypeId === savedClaimType.value)
                    .map((doc) => doc.documentDesc);
                setSelectedDocDescs(selectedDocs);
            }
        }
    }, [claimTypes, docPerClaimTypes]);

    useEffect(() => {
        mapDocumentPerClaimType();
    }, []);

    return (
        <>
            {loading && (
                <Loading>
                    <Spinner animation="border" variant="light" />
                </Loading>
            )}
            <StyledContainer fluid>
                <Row className="pt-3">
                    <Title>File a Claim</Title>
                </Row>
                <StyledForm>
                    <Row>
                        <Description>
                            What are you submitting a claim for/nature of loss?
                            <InfoContainer>
                                <i className="bi bi-info-circle" style={{ padding: '2px', fontSize: '18px' }}></i>
                                <StyledDescription className="info-description">
                                    Kung ang iyong pagkawala ay may kasamang maraming sanhi (hal., lindol +
                                    sunog/kidlat), mag-file ng hiwalay na report para sa bawat uri ng pagkawala para sa
                                    mas malinaw na pagproseso.
                                </StyledDescription>
                            </InfoContainer>
                        </Description>
                    </Row>
                    <Row>
                        <Col className="d-flex">
                            <DescriptionLite>
                                If your loss involves multiple causes (e.g., earthquake + fire/lightning), file a
                                separate report for each type of loss for clearer processing.
                            </DescriptionLite>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col sm={4}>
                            <Select
                                value={selectedClaimType}
                                placeholder="Please Select from the list"
                                isSearchable
                                options={claimTypeOptions}
                                onChange={handleClaimTypeChange}
                            />
                        </Col>
                        <Col sm={8}>
                            <Tabs defaultActiveKey="description" id="uncontrolled-tab-example">
                                <Tab eventKey="description">
                                    <div className="pt-2">
                                        {selectedClaimType && <StyleTitle>{selectedClaimType.label}</StyleTitle>}
                                        {selectedDocDescs.length > 0 ? (
                                            selectedDocDescs.map((desc, index) => (
                                                <p key={index}>
                                                    <i
                                                        className="bi bi-check-lg"
                                                        style={{ fontSize: '26px', color: 'black' }}
                                                    ></i>
                                                    {desc}
                                                </p>
                                            ))
                                        ) : (
                                            <p></p>
                                        )}
                                    </div>
                                </Tab>
                            </Tabs>
                        </Col>
                    </Row>
                    <Row>
                        <div className="gap-2 justify-content-between mt-2">
                            <StyledSubmitButton
                                className="btn-submit me-2"
                                variant="secondary"
                                type="button"
                                onClick={() => {
                                    navigate('/home');
                                }}
                            >
                                PREVIOUS
                            </StyledSubmitButton>
                            <StyledSubmitButton2
                                className="btn-submit"
                                variant="secondary"
                                type="button"
                                onClick={handleFormSubmit}
                            >
                                NEXT
                            </StyledSubmitButton2>
                        </div>
                    </Row>
                    <Row>
                        <Col>
                            <DescriptionLite2>
                                <strong>Need Home Assistance?</strong> Para sa emergency plumbing, electrical, lock, o
                                glass repair, {' '}
                                <a href="https://m.me/pacificoassistance" target="_blank">
                                    <strong>Click here</strong>
                                </a>{' '}
                                to initiate claim via Messenger.
                            </DescriptionLite2>
                        </Col>
                    </Row>
                </StyledForm>
            </StyledContainer>
        </>
    );
}
